/**
 * Used by — the AI Services and configs whose flows run this agent (task 1311).
 *
 * The Advanced → Agents detail page's answer to "what does a change here
 * touch?". It reads `GET /agents/:id/dependents`, the same read the delete
 * dialog uses, so the two can never disagree about what uses the agent:
 *
 *   - `collaborationConfigs` are the configs whose definition names the agent,
 *     each with the agent node ids that run it (`nodeIds`);
 *   - `endpoints` are the AI Services pointed at those configs, each naming the
 *     config it runs (`collaborationConfigId`);
 *   - `blocking` are the services a delete would break. For an agent that is
 *     every service that runs it, which is why its rows carry "running".
 *
 * One row per (config, service) pair; a config no service runs still gets a row,
 * with a "no service" chip and a link to the config itself. Where the agent sits
 * in each flow, and any node-level Model override, is on the service's Config
 * tab — this panel links there rather than repeating it.
 */
import { Link } from 'react-router-dom';
import { useDependents } from '@/hooks/useDependents';
import type { DependentEndpointRef, DependentRef } from '@/types/dependents';

interface AgentUsedByPanelProps {
  agentId: string;
}

interface UsedByRow {
  key: string;
  config: DependentRef;
  service: DependentEndpointRef | null;
  running: boolean;
}

const chipBase = 'ml-1.5 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium';

const linkClasses =
  'text-xs font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300';

export function AgentUsedByPanel({ agentId }: AgentUsedByPanelProps) {
  const { data, isLoading, isError } = useDependents('agents', agentId);

  const configs = data?.dependents.collaborationConfigs ?? [];
  const services = data?.dependents.endpoints ?? [];
  const blockingIds = new Set((data?.blocking ?? []).map((endpoint) => endpoint.id));

  const rows: UsedByRow[] = configs.flatMap((config): UsedByRow[] => {
    const running = services.filter((service) => service.collaborationConfigId === config.id);
    if (running.length === 0) {
      return [{ key: config.id, config, service: null, running: false }];
    }
    return running.map((service) => ({
      key: `${config.id}:${service.id}`,
      config,
      service,
      running: blockingIds.has(service.id),
    }));
  });

  return (
    <section
      data-testid="agent-used-by-panel"
      className="flex flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
    >
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Used by</h3>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        The AI Services and configs whose flows run this agent
      </p>

      {isLoading ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : isError ? (
        <p
          data-testid="agent-used-by-error"
          className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
        >
          What uses this agent could not be loaded.
        </p>
      ) : rows.length === 0 ? (
        <p data-testid="agent-used-by-empty" className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          No service runs this agent yet.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs" data-testid="agent-used-by-table">
            <thead>
              <tr className="border-b border-gray-200 text-left text-[11px] uppercase tracking-wide text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="pb-2 pr-3 font-bold">AI Service</th>
                <th className="pb-2 pr-3 font-bold">Config</th>
                <th className="pb-2 pr-3 font-bold">Node</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  data-testid={`agent-used-by-row-${row.key}`}
                  className="border-b border-gray-100 dark:border-gray-700/60"
                >
                  <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">
                    {row.service ? (
                      <>
                        <Link
                          to={`/services/${row.service.id}`}
                          className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300"
                        >
                          {row.service.name}
                        </Link>
                        {row.running && (
                          <span
                            data-testid="agent-used-by-running"
                            className={`${chipBase} border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200`}
                          >
                            running
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                    {row.config.name}
                    {!row.service && (
                      <span
                        data-testid="agent-used-by-no-service"
                        className={`${chipBase} border-gray-300 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/50 dark:text-gray-300`}
                      >
                        no service
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-mono text-gray-700 dark:text-gray-300">
                    {row.config.nodeIds && row.config.nodeIds.length > 0 ? row.config.nodeIds.join(', ') : '—'}
                  </td>
                  <td className="py-2 text-right">
                    <Link
                      to={row.service ? `/services/${row.service.id}?tab=config` : `/configs/${row.config.id}`}
                      className={linkClasses}
                    >
                      Config →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pins the footer to the card's foot when the panel stretches to its row. */}
      <div className="flex-1" />
      <p
        data-testid="agent-used-by-footer"
        className="mt-4 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400"
      >
        Where the agent sits in each flow, and any node-level Model override, is on that
        service&rsquo;s Config tab. Saving changes what these services run on their next call,
        unless the service pins a published version.
      </p>
    </section>
  );
}

export default AgentUsedByPanel;
